import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { IoChevronDownOutline, IoChevronUpOutline } from "react-icons/io5";

const MaterialButton = ({
  name,
  options,
  onSubmit,
  icon,
  activeSelections = [],
}) => {
  const [materialOption, setMaterialOption] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setMaterialOption(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const handleMaterialButtonClick = (event) => {
    event.stopPropagation();
    setMaterialOption(!materialOption);
  };

  const toggleSelection = (option) => {
    // Pass the option to the parent component for toggling
    onSubmit(option);
  };

  // Calculate if any options are selected for the button appearance
  const hasActiveSelections = activeSelections.length > 0;

  return (
    <div style={{ position: "relative" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginRight: 10,
          borderRadius: 8,
          backgroundColor: hasActiveSelections ? "#e8f4f8" : "#f6f6f6",
          border: `1px solid ${hasActiveSelections ? "#a0d2e5" : "#e0e0e0"}`,
          padding: "10px 14px",
          cursor: "pointer",
          transition: "all 0.2s ease",
          boxShadow: hasActiveSelections
            ? "0 2px 4px rgba(0, 0, 0, 0.05)"
            : "none",
          minWidth: 120,
        }}
        onClick={handleMaterialButtonClick}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = hasActiveSelections
            ? "#d6edf5"
            : "#f0f0f0";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = hasActiveSelections
            ? "#e8f4f8"
            : "#f6f6f6";
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
          }}
        >
          {icon && (
            <span
              style={{ marginRight: 10, display: "flex", alignItems: "center" }}
            >
              {icon}
            </span>
          )}
          <span
            style={{
              fontWeight: hasActiveSelections ? "500" : "normal",
              color: hasActiveSelections ? "#2c7da0" : "#555",
              fontSize: "14px",
            }}
          >
            {name}
            {hasActiveSelections && ` (${activeSelections.length})`}
          </span>
        </div>
        <span style={{ marginLeft: 8, display: "flex", alignItems: "center" }}>
          {materialOption ? (
            <IoChevronUpOutline
              size={16}
              color={hasActiveSelections ? "#2c7da0" : "#777"}
            />
          ) : (
            <IoChevronDownOutline
              size={16}
              color={hasActiveSelections ? "#2c7da0" : "#777"}
            />
          )}
        </span>
      </div>

      {materialOption && (
        <div
          ref={dropdownRef}
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            left: 0,
            padding: 0,
            backgroundColor: "#FFF",
            borderRadius: 8,
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)",
            width: 240,
            zIndex: 10,
            overflow: "hidden",
            border: "1px solid #eaeaea",
            animation: "fadeIn 0.2s ease",
          }}
        >
          <div
            style={{ padding: "10px 0", maxHeight: "280px", overflowY: "auto" }}
          >
            {options.map((item, index) => (
              <div
                key={index}
                style={{
                  padding: "8px 16px",
                  cursor: "pointer",
                  backgroundColor: activeSelections.includes(item)
                    ? "#f2f9fc"
                    : "transparent",
                  transition: "background-color 0.15s ease",
                  borderBottom:
                    index < options.length - 1 ? "1px solid #f5f5f5" : "none",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor =
                    activeSelections.includes(item) ? "#e6f4fa" : "#f9f9f9";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor =
                    activeSelections.includes(item) ? "#f2f9fc" : "transparent";
                }}
              >
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    cursor: "pointer",
                    padding: "4px 0",
                    fontSize: "14px",
                    color: activeSelections.includes(item) ? "#2c7da0" : "#444",
                  }}
                >
                  <div
                    style={{
                      position: "relative",
                      width: 18,
                      height: 18,
                      marginRight: 12,
                      border: `2px solid ${
                        activeSelections.includes(item) ? "#2c7da0" : "#aaa"
                      }`,
                      borderRadius: 4,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.15s ease",
                      backgroundColor: activeSelections.includes(item)
                        ? "#2c7da0"
                        : "transparent",
                    }}
                  >
                    {activeSelections.includes(item) && (
                      <div
                        style={{
                          position: "absolute",
                          top: "1px",
                          left: "5px",
                          width: "5px",
                          height: "10px",
                          borderRight: "2px solid white",
                          borderBottom: "2px solid white",
                          transform: "rotate(45deg)",
                        }}
                      />
                    )}
                    <input
                      type="checkbox"
                      checked={activeSelections.includes(item)}
                      onChange={() => toggleSelection(item)}
                      style={{
                        opacity: 0,
                        position: "absolute",
                        cursor: "pointer",
                        width: "100%",
                        height: "100%",
                        zIndex: 2,
                      }}
                    />
                  </div>
                  <span>{item}</span>
                </label>
              </div>
            ))}
          </div>
        </div>
      )}
      <style>
        {`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-5px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                `}
      </style>
    </div>
  );
};

MaterialButton.propTypes = {
  name: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  onSubmit: PropTypes.func.isRequired,
  icon: PropTypes.node,
  activeSelections: PropTypes.array,
};

export default MaterialButton;
